import { ClipboardEvent, useCallback, useMemo, useState } from 'react';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';

import { cn } from '@mint-lib/tw-utils';
import DOMPurify from 'dompurify';
import { BaseEditor, createEditor, Editor, Node, Transforms } from 'slate';

import { renderEditorElement } from '../../common/TextArea/renderEditorElement.jsx';
import { renderEditorPlaceholder } from '../../common/TextArea/renderEditorPlaceholder.jsx';
import {
  countTextLength,
  getInitialValue,
  htmlToSlateNodes,
  renderEditorLeaf,
  withCharacterLimit,
  type CustomSlateNode,
} from '../../common/TextArea/utils.jsx';
import { InputHelperText } from '../../lib/input.jsx';
import FormatButtonControls from './FormatButtonControls.jsx';
import {
  editorVariants,
  LIST_TYPES,
  TextStyle,
} from './TextAreaWithControls.constants.jsx';
import { serializeSlateToHtml } from './TextAreaWithControls.utils.jsx';
import TextStyleSelect from './TextStyleSelect.jsx';

type CustomEditor = BaseEditor & ReactEditor;

export type TextAreaWithControlsProps = {
  helperText?: string;
  /** max length */
  maxLength?: number;
  /** If 'true' - disabled. */
  isDisabled?: boolean;
  /** State of the text area: warning, error, default. */
  color?: 'warning' | 'error' | 'default';
  /** readOnly  */
  isReadOnlyAccess?: boolean;
  /** The initial value of the text area. */
  value?: string;
  /** Height of the input area. */
  inputAreaHeight?: number;
  /** minHeight of the input area. */
  minHeight?: number;
  /** Function to handle saving the input value. */
  onSaveInputValue: (
    inputValue: string,
    serializeSlateToHtml: (node: CustomSlateNode) => string,
  ) => void;
  /** Placeholder */
  placeholder?: string;
};

/**
 * Component for displaying a text area with formatting controls.
 *
 * @example
 * Basic usage:
 *
 * const handleSaveInputValueFunction = (inputValue, serializeSlateToHtml) => {
 *   const htmlValue = serializeSlateToHtml({ children: JSON.parse(inputValue) });
 * }
 *
 * <TextAreaWithControls
 *   helperText="Enter your text here"
 *   maxLength={100}
 *   onSaveInputValue={handleSaveInputValueFunction}
 *   value={inputValue}
 * />
 */

const TextAreaWithControls = ({
  value = '',
  onSaveInputValue,
  color = 'default',
  helperText,
  maxLength,
  isReadOnlyAccess = false,
  isDisabled = false,
  inputAreaHeight,
  minHeight = 20,
  placeholder,
}: TextAreaWithControlsProps) => {
  const [inputValue, setInputValue] = useState<Node[]>(getInitialValue(value));
  const [isBlurBlocked, setIsBlurBlocked] = useState(false);
  const [currentTextStyle, setCurrentTextStyle] = useState<string>(
    TextStyle.normal,
  );
  const editor = useMemo(
    () =>
      withCharacterLimit(withReact(createEditor() as ReactEditor), maxLength),
    [maxLength],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        maxLength &&
        countTextLength(editor.children) >= maxLength &&
        !['Backspace', 'Delete'].includes(event.key)
      ) {
        event.preventDefault();
        return false;
      }
      event.stopPropagation();
    },
    [editor, maxLength],
  );

  // const preparedValue =
  //   typeof inputValue === 'string'
  //     ? [
  //         {
  //           type: 'placeholder',
  //           children: [
  //             {
  //               text: inputValue,
  //             },
  //           ],
  //         },
  //       ]
  //     : inputValue;

  const length = useMemo(() => {
    return countTextLength(inputValue);
  }, [inputValue]);

  const isBlockActive = useCallback(
    (editorProps: BaseEditor, format: string) => {
      const [match] = Editor.nodes(editorProps, {
        match: (n) => {
          const CustomSlateNode = n as CustomSlateNode;
          return CustomSlateNode.type === format;
        },
      });
      return !!match;
    },
    [],
  );

  const toggleList = useCallback(
    (format: string) => () => {
      const isActive = isBlockActive(editor, format);
      const isList = LIST_TYPES.includes(format);

      Transforms.unwrapNodes(editor, {
        match: (n) => {
          const CustomSlateNode = n as CustomSlateNode;
          return (
            !!CustomSlateNode.type && LIST_TYPES.includes(CustomSlateNode.type)
          );
        },
        split: true,
      });
      if (isActive) {
        Transforms.setNodes(editor, {
          type: TextStyle.normal,
          children: [],
        });
      } else {
        Transforms.setNodes(editor, {
          type: isList ? 'list-item' : format,
          children: [],
        });
      }

      if (!isActive && isList) {
        const block = { type: format, children: [] };
        Transforms.wrapNodes(editor, block);
      }

      onSaveInputValue(JSON.stringify(editor.children), serializeSlateToHtml);
    },
    [editor, isBlockActive, onSaveInputValue],
  );

  const selectTextStyle = (style: string) => {
    toggleList(style)();
    setCurrentTextStyle(style);
  };

  const getActiveTextStyle = useCallback(
    (editorInstance: BaseEditor): string => {
      const styleTypes = Object.values(TextStyle);
      for (const style of styleTypes) {
        const [match] = Editor.nodes(editorInstance, {
          match: (n) => (n as CustomSlateNode).type === style,
        });
        if (match) return style;
      }
      return TextStyle.normal;
    },
    [],
  );

  const onChange = (val: Node[]) => {
    setInputValue(val);
    setCurrentTextStyle(getActiveTextStyle(editor));
  };

  // We also can use editor.insertData instead of onPaste, but it has open issue with pasting
  // html layout and from time to time it may crash
  const onPaste = (
    event: ClipboardEvent<HTMLDivElement>,
    editor: CustomEditor,
  ) => {
    event.preventDefault();
    const { clipboardData } = event;
    const html = clipboardData?.getData('text/html');
    const sanitizedHtml = DOMPurify.sanitize(html ?? '');
    const parsed = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
    const fragmentsRaw = htmlToSlateNodes(parsed.body);

    Transforms.insertFragment(editor, fragmentsRaw);
    return sanitizedHtml;
  };

  //onBlur can`t be covered by tests, as contenteditable (Slate) is not supported in JSDOM: blur will not fire. More detailed here: https://stackoverflow.com/questions/64340504/how-to-test-contenteditable-events-with-react-testing-library
  const onBlur = useCallback(() => {
    if (isDisabled || isBlurBlocked) return;

    onSaveInputValue(JSON.stringify(editor.children), serializeSlateToHtml);
  }, [isDisabled, isBlurBlocked, onSaveInputValue, editor.children]);

  const minHeightClasses = minHeight ? `min-h-[${minHeight}px]` : 'min-h-20';
  const heightClasses = inputAreaHeight ? `h-[${inputAreaHeight}px]` : 'h-auto';

  return (
    <Slate editor={editor} value={inputValue} onChange={onChange}>
      {!isReadOnlyAccess && (
        <div className="top-0 mb-1 flex h-8 items-center justify-start gap-2">
          <TextStyleSelect
            isDisabled={isDisabled}
            readOnly={isReadOnlyAccess}
            selectTextStyle={selectTextStyle}
            setIsBlurBlocked={setIsBlurBlocked}
            value={currentTextStyle}
          />
          <FormatButtonControls isDisabled={isDisabled} />
        </div>
      )}
      <div id="editorWrapper" className={cn(minHeightClasses, heightClasses)}>
        <Editable
          className={cn(
            editorVariants({
              color: color,
            }),
          )}
          id="editor"
          data-testid="textbox"
          disabled={isDisabled}
          onBlur={onBlur}
          placeholder={placeholder ?? 'Write your brief here'}
          renderElement={renderEditorElement}
          renderLeaf={renderEditorLeaf}
          renderPlaceholder={renderEditorPlaceholder}
          onPaste={(event) => onPaste(event, editor)}
          readOnly={isDisabled || isReadOnlyAccess}
          onKeyDown={onKeyDown}
        />
      </div>
      {helperText && <InputHelperText helperText={helperText} color={color} />}
      {maxLength && (
        <InputHelperText
          disabled={isDisabled}
          helperText={`${length}/${maxLength}`}
          color={'default'}
        />
      )}
    </Slate>
  );
};

export default TextAreaWithControls;
